import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import { landService } from '../services/landService';
import { fetchSubcounties } from '../services/subcountyService';
import { 
    getFinancialYearData, 
    formatToLocalTime, 
    formatTodayTitle, 
    formatCurrentMonthTitle, getFYStartYear 
} from '../utils/dateHelper';
import { calculatePerformance, getHeatmapClass } from '../utils/analysisUtils'; // For performance calculations and heatmap classes
//import { fetchRevenueTargets } from '../services/revTargetService'; // For fetching monthly targets
import { fetchSubCountyAnalysis } from '../services/analysisService';
import { fetchCountyAnalysis } from '../services/analysisService';
import { RefreshCcw, Map, Home, TrendingUp, Clock, Calendar } from 'lucide-react';

const LandRatesDashboard = () => {
    const { user } = useAuth();
    const fyOptions = getFinancialYearData();
    
    // 1. Filter States
    const [selectedFY, setSelectedFY] = useState(fyOptions.current);
    const [selectedSC, setSelectedSC] = useState(''); 
    const [subcounties, setSubcounties] = useState([]);
    
    // 2. Data States
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState({}); // Local state to hold data for exports
      const monthsInOrder = [
        'July', 'August', 'September', 'October', 'November', 'December',
        'January', 'February', 'March', 'April', 'May', 'June'
    ];
    // Fetch sub-counties and handle restricted user access
    useEffect(() => {
        fetchSubcounties().then(scs => {
            setSubcounties(scs);
            if (user?.sub_county_id) {
                setSelectedSC(user.sub_county_id);
            }
        });
    }, [user?.sub_county_id]);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        try {
            // Procedure expects NULL or ID
            const stats = await landService.getDashboardStats(selectedFY, selectedSC);
            setData(stats);
        } catch (err) {
            console.error("Land Dashboard Load Error:", err);
        } finally {
            setLoading(false);
        }
    }, [selectedFY, selectedSC]);

    const loadMonthlyAnalysis = useCallback(async () => {
        // Convert "FY2025/2026" -> 2025
            const startYear = getFYStartYear(selectedFY);
            setLoading(true);
            try {
                // Use the existing service from analysisService.js
                let rawData;
                if (selectedSC) {
                    // Fetch specific Sub-County data
                    rawData = await fetchSubCountyAnalysis(startYear, selectedSC);
                } else {
                    // Fetch Entire County aggregate data
                    rawData = await fetchCountyAnalysis(startYear);
                }
                // Group data by stream as required for the SBP Monthly table
                const grouped = rawData.reduce((acc, row) => {
                    if (!acc[row.rev_stream]) acc[row.rev_stream] = [];
                    acc[row.rev_stream].push(row);
                    return acc;
                }, {});
    
                setReportData(grouped);
            } catch (error) {
                console.error("Failed to fetch monthly analysis data", error);
            } finally {
                setLoading(false);
            }
        }, [selectedFY, selectedSC]);
    // Re-run whenever FY or Sub-County changes
    useEffect(() => { 
        loadMonthlyAnalysis();
        loadDashboard(); 
    }, [loadMonthlyAnalysis, loadDashboard, selectedFY, selectedSC]);
    // Variables to track running totals
    let cumulativeActual = 0;
    let cumulativeTarget = 0;
    // Process the display data
    const processedMonthlyData = monthsInOrder.map(month => {
            const landRatesStreamData = reportData['Land Rates'] || [];
            const record = landRatesStreamData.find(r => r.calendar_month === month);
    
            const actual = record ? Number(record.amount_fy0 || 0) : 0;
            const target = record ? Number(record.monthly_target || 0) : 0;
    
            // Update running totals
            cumulativeActual += actual;
            cumulativeTarget += target;
    
            return {
                month,
                actual,
                target,
                performance: calculatePerformance(actual, target),
                cumulativeActual,
                cumulativeTarget,
                cumulativePerformance: calculatePerformance(cumulativeActual, cumulativeTarget)
            };
    });
    const subCountyName = selectedSC 
        ? subcounties.find(sc => String(sc.id) === String(selectedSC))?.name 
        : 'Entire County';

    if (loading && !data) {
        return (
            <div className="flex h-64 items-center justify-center flex-col gap-4">
                <RefreshCcw className="animate-spin text-emerald-600" size={40} />
                <p className="text-slate-500 font-bold animate-pulse uppercase">Synchronizing Land Records...</p>
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-fadeIn p-2">
            {/* Header & Filter Section */}
            <div className="bg-white px-8 py-4 rounded-b-2xl shadow-sm border flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex flex-col">
                    <h1 className="text-xl font-black text-slate-800 flex items-center flex-wrap leading-tight">
                        <Map className="mr-3 text-emerald-600" size={22} />
                        Land Rates Performance
                        <span className="ml-3 text-emerald-600 text-sm font-bold">[{subCountyName}]</span>
                         <span className="ml-2 text-blue-500 text-sm font-bold">[{selectedFY}]</span>
                    </h1>
                    <span className="text-xs font-bold text-red-600 mt-1">
                        Last Updated: {data?.lastUpdated ? formatToLocalTime(data.lastUpdated) : '---'}
                    </span>
                </div>

                <div className="flex items-center space-x-4">
                    {/* FY Selection Pill */}
                    <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase mr-3">FY Period</span>
                        <select 
                            value={selectedFY} 
                            onChange={(e) => setSelectedFY(e.target.value)}
                            className="bg-transparent text-sm font-black text-slate-700 outline-none cursor-pointer"
                        >
                            <option value={fyOptions.previous}>{fyOptions.previous}</option>
                            <option value={fyOptions.current}>{fyOptions.current}</option>
                        </select>
                    </div>

                    {/* Sub-County/Region Selection Pill */}
                    <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase mr-3">Region</span>
                        <select 
                            value={selectedSC} 
                            onChange={(e) => setSelectedSC(e.target.value)}
                            disabled={!!user?.sub_county_id} // Lock if user belongs to specific SC
                            className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer min-w-[140px] disabled:opacity-70"
                        >
                            {!user?.sub_county_id && <option value="">Entire County</option>}
                            {subcounties.map(sc => (
                                <option key={sc.id} value={sc.id}>{sc.name}</option>
                            ))}
                        </select>
                    </div>

                    <button 
                        onClick={loadDashboard} 
                        className="p-2.5 bg-blue-600 text-white rounded-xl shadow-md hover:bg-blue-700 active:scale-95 transition-all"
                    >
                        <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 px-1">
                <StatCard 
                    title="Total Land Plots" 
                    value={data?.allPlots} 
                    subValue={`H1: ${data?.plotsH1} | H2: ${data?.plotsH2}`} 
                    icon={<Home />} 
                    color="emerald" 
                />
                <StatCard 
                    title={`Today (${formatTodayTitle(data?.currentDate)})`} 
                    value={data?.todayRev} 
                    isMoney 
                    icon={<Clock size={18}/>} 
                    color="blue" 
                />
                <StatCard 
                    title={`This Month (${formatCurrentMonthTitle(data?.lastUpdated)})`} 
                    value={data?.monthRev} 
                    isMoney 
                    icon={<Calendar size={18}/>} 
                    color="indigo" 
                />
                <StatCard 
                    title={`FY Realized [${selectedFY}]`} 
                    value={data?.paidRevTotal} 
                    isMoney 
                    icon={<TrendingUp />} 
                    color="emerald" 
                />
            </div>

            {/* Pipeline and Revenue Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PerformanceTable 
                    title="Land Plot and Land Rates Invoice Approval Pipeline"
                    rows={[
                        { label: 'Plots Not Approved', total: data?.pendTotal, h1: data?.pendH1, h2: data?.pendH2 },
                        { label: 'Plots Approved', total: data?.appTotal, h1: data?.appH1, h2: data?.appH2, color: 'text-emerald-600' },
                        { label: 'Plots Deferred', total: data?.defTotal, h1: data?.defH1, h2: data?.defH2, color: 'text-amber-600' },
                        { label: 'Plots Pre-Approved', total: data?.preTotal, h1: data?.preH1, h2: data?.preH2, color: 'text-blue-600' },
                        { label: 'Approved Not Invoiced', total: data?.missingInvTotal, h1: data?.missingInvH1, h2: data?.missingInvH2, color: 'text-red-900' },
                        { label: 'Invoices Approved', total: data?.invAppCountTotal, h1: data?.invAppCountH1, h2: data?.invAppCountH2, color: 'text-purple-600' },
                        { label: 'Invoices Not Approved', total: data?.invPendAppCountTotal, h1: data?.invPendAppCountH1, h2: data?.invPendAppCountH2, color: 'text-red-600' },
                    ]}
                />
                
                <PerformanceTable 
                    title="Land Rates Revenue Collection (FY)"
                    isMoney
                    rows={[
                        { label: 'Paid Revenue', total: data?.paidRevTotal, h1: data?.paidRevH1, h2: data?.paidRevH2, color: 'text-green-600' },
                        { label: 'Approved Invoices Paid', total: data?.paidCountTotal, h1: data?.paidCountH1, h2: data?.paidCountH2, color: 'text-green-600' },
                        { label: 'Pending Revenue', total: data?.unpaidRevTotal, h1: data?.unpaidRevH1, h2: data?.unpaidRevH2, color: 'text-red-600' },
                        { label: 'Approved Invoices Not Paid', total: data?.unpaidCountTotal, h1: data?.unpaidCountH1, h2: data?.unpaidCountH2, color: 'text-red-600' },
                    ]}
                />
            </div>
             <MonthlyLRTable 
                data={processedMonthlyData} 
                selectedFY={selectedFY}
                subCountyName={subCountyName} 
            />
        </div>
    );
};

// Reusable Components
const StatCard = ({ title, value, subValue, icon, color, isMoney }) => (
    <div className={`bg-white px-4 py-3 rounded-xl border-l-4 border-${color}-500 shadow-sm flex flex-col justify-center`}>
        <div className="flex justify-between items-center text-slate-400 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider">{title}</span>
            <div className={`text-${color}-500`}>{icon}</div>
        </div>
        <div className="text-xl font-black text-slate-800 leading-tight">
            {isMoney ? `KES ${Number(value || 0).toLocaleString()}` : Number(value || 0).toLocaleString()}
        </div>
        {subValue && (
            <div className="text-[11px] text-slate-400 mt-0.5 font-semibold">
                {subValue}
            </div>
        )}
    </div>
);

const PerformanceTable = ({ title, rows, isMoney }) => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-6 py-2.5 border-b font-bold text-slate-700 text-sm">{title}</div>
        <table className="w-full text-left text-xs">
            <thead className="bg-slate-50/30 text-slate-400 uppercase text-[9px] font-black tracking-wider">
                <tr>
                    <th className="px-6 py-2">Category</th>
                    <th className="px-6 py-2 text-center">July - Dec (H1)</th>
                    <th className="px-6 py-2 text-center">Jan - June (H2)</th>
                    <th className="px-6 py-2 text-right pr-10 text-blue-600">Total</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                {rows.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                        <td className={`px-6 py-2 font-bold ${row.color || 'text-slate-700'}`}>{row.label}</td>
                        {/* Updated H2 Column Logic */}
                        <td className="px-6 py-2 text-center text-slate-500">
                            {isMoney && row.label !== 'Approved Invoices Paid' && row.label !== 'Approved Invoices Not Paid' 
                                ? `KES ${Number(row.h1 || 0).toLocaleString()}` 
                                : (row.h1 || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-2 text-center text-slate-500">
                            {isMoney && row.label !== 'Approved Invoices Paid' && row.label !== 'Approved Invoices Not Paid' 
                                ? `KES ${Number(row.h2 || 0).toLocaleString()}` 
                                : (row.h2 || 0).toLocaleString()}
                        </td>

                        {/* Apply the same to the Total Column */}
                        <td className={`px-6 py-2 text-right pr-10 font-black ${row.color || 'text-slate-800'}`}>
                            {isMoney && row.label !== 'Approved Invoices Paid' && row.label !== 'Approved Invoices Not Paid' 
                                ? `KES ${Number(row.total || 0).toLocaleString()}` 
                                : (row.total || 0).toLocaleString()}
                        </td>
                    </tr>
                    ))}
                    </tbody>
                </table>
            </div>
);
const MonthlyLRTable = ({ data, selectedFY, subCountyName, isLoading }) => (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden col-span-full relative">
        {/* Loading Overlay */}
        {isLoading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <RefreshCcw className="animate-spin text-blue-600 mb-2" size={24} />
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Syncing Monthly Data...</span>
                </div>
            </div>
        )}

        <div className="bg-slate-50 px-6 py-4 border-b flex items-center justify-between">
            <h3 className="font-bold text-slate-700 flex items-center">
                <Calendar className="mr-2 text-blue-500" size={16} />
                Monthly Land Rates Revenue Performance 
                <span className="ml-3 text-emerald-600 text-sm font-bold">[{subCountyName}]</span>
                <span className="ml-2 text-blue-500 text-sm font-bold">[{selectedFY}]</span>
            </h3>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">July - June Cycle</span>
        </div>
        
        <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-slate-50/50 text-slate-400 text-[10px] font-black uppercase tracking-widest">
                        <th className="px-6 py-4 text-left border-r w-44">Metric</th>
                        {data.map(m => <th key={m.month} className="px-4 py-4 text-center border-r">{m.month}</th>)}
                        <th className="px-6 py-4 text-center bg-blue-50 text-blue-600">Total(FYTD)</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {/* Actuals Row */}
                    <tr>
                        <td className="px-6 py-4 font-bold text-slate-700 border-r bg-slate-50/10">Actual (KES)</td>
                        {data.map((m, i) => (
                            <td key={i} className="px-4 py-4 text-center font-semibold text-slate-800 border-r">
                                {m.actual.toLocaleString()}
                            </td>
                        ))}
                        <td className="px-6 py-4 text-center font-black text-blue-700 bg-blue-50/30">
                            {data[data.length - 1]?.cumulativeActual.toLocaleString()}
                        </td>
                    </tr>
                    {/* Targets Row */}
                    <tr>
                        <td className="px-6 py-4 font-bold text-slate-600 border-r bg-slate-50/10 italic">Target (KES)</td>
                        {data.map((m, i) => (
                            <td key={i} className="px-4 py-4 font-semibold text-center text-slate-400 italic border-r">
                                {m.target.toLocaleString()}
                            </td>
                        ))}
                        <td className="px-6 py-4 text-center font-bold text-slate-500 bg-blue-50/30 italic">
                            {data[data.length - 1]?.cumulativeTarget.toLocaleString()}
                        </td>
                    </tr>
                    {/* Achievement Heatmap */}
                    <tr>
                        <td className="px-6 py-4 font-bold text-slate-700 border-r bg-slate-50/10">Achievement %</td>
                        {data.map((m, i) => (
                            <td key={i} className="px-4 py-4 text-center border-r">
                                <div className={`inline-block px-3 py-1 rounded-lg text-[11px] font-black ${getHeatmapClass(m.performance)}`}>
                                    {m.performance.toFixed(1)}%
                                </div>
                            </td>
                        ))}
                        <td className="px-6 py-4 text-center bg-blue-50/30">
                            <div className={`inline-block px-4 py-1.5 rounded-xl text-xs font-black shadow-sm ${getHeatmapClass(data[data.length - 1]?.cumulativePerformance)}`}>
                                {data[data.length - 1]?.cumulativePerformance.toFixed(1)}%
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
);
export default LandRatesDashboard;